import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cn from "classnames";
import moment from "moment";

import KeywordPicker from "../keyword-picker";
import RegionPicker from "../region-picker";
import CalendarPicker from "../calendar-picker";
import SeatPicker from "../seat-picker";

import {
  AddGroupIcon,
  CalendarIcon,
  HomeIcon,
  LocationIcon,
  LogoIcon,
  ProfileIcon,
  SearchIcon,
  CloseIcon,
  Close2Icon,
  HomeActiveIcon,
  SearchActiveIcon,
  PhoneIcon
} from "../../icons";

import { useTranslation } from "react-i18next";

import { useClickAway } from "ahooks";

import { observer } from "mobx-react-lite";

import {
  useDismissOnOutsidePointer,
  useExclusivePopoverToggles,
  useHeaderCompact,
  usePopoverPosition,
  usePresenceTransition,
} from "./hooks";

import { useFilterContext } from "./filter-context";

function onRestaurantLoginClick() {
  window.open("https://assistant.diningcity.asia", "_blank");
}
function onPartnerClick() {
  window.open("https://partnerwithus.diningcity.asia", "_blank");
}

const noop = () => {};

// Desktop 只管理自己的 UI 状态（popover、搜索、日期、人数等）
const DesktopHeadBar = () => {
  const { t } = useTranslation();

  const {
    searchState: filterState,
    setSearchState: setFilterState,
    onSearchBtnClick,
    // 共享数据
    regionList,
    currentCity,
    pageCity,
    searchRegionList,

    // 共享动作
    getPosition,
    searchRegion,
    onHomeClick,
    onExploreClick,
    onUserClick,
    onDownloadClick,
    onCityClick,

    // 共享状态
    activeTab,
    logo,
  } = useFilterContext();

  const headerRef = useRef(null);
  const regionBtnRef = useRef(null);
  const regionPopoverRef = useRef(null);
  const datesBtnRef = useRef(null);
  const datesPopoverRef = useRef(null);
  const guestsBtnRef = useRef(null);
  const guestsPopoverRef = useRef(null);
  const searchRef = useRef(null);
  const fieldRef = useRef(null);
  const searchInputRef = useRef(null);
  const keywordPopoverRef = useRef(null);

  const [isRegionOpen, setIsRegionOpen] = useState(false);
  const [isDatesOpen, setIsDatesOpen] = useState(false);
  const [isGuestsOpen, setIsGuestsOpen] = useState(false);
  const [isKeywordOpen, setIsKeywordOpen] = useState(false);
  const [inputFocus, setInputFocus] = useState(false);

  const touchLastYRef = useRef(0);

  useEffect(() => {
    // Navigating away should not keep body locked.
    if (!isRegionOpen && !isDatesOpen && !isGuestsOpen) return;
    setIsRegionOpen(false);
    setIsDatesOpen(false);
    setIsGuestsOpen(false);
  }, [activeTab]);

  useClickAway(() => {
    setInputFocus(false);
    setIsKeywordOpen(false);
  }, searchInputRef);

  const onRegionClick = (city) => {
    onCityClick(city);
    setIsRegionOpen(false);
    setSearchInputValue("");
  };

  const guestsCount = useMemo(() => {
    const n = Number(filterState.seats || 0);
    return Number.isFinite(n) ? n : 0;
  }, [filterState.seats]);

  const setGuestsCount = useCallback(
    (val) => {
      const n = Number(val || 0);
      setFilterState({ seats: n > 0 ? String(n) : "" });
    },
    [setFilterState],
  );

  const searchQuery = filterState.q || "";
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);

  useEffect(() => {
    setSearchInputValue(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    // Avoid wiping the input when URL already provides a keyword (e.g. /browse?q=xxx).
    // Otherwise this effect can run after the searchQuery sync effect and overwrite it.
    if (!searchQuery) setSearchInputValue("");
  }, [activeTab, searchQuery]);

  const date = useMemo(() => {
    if (!filterState.date) return null;
    if (filterState.date instanceof Date) return filterState.date;
    const m = moment(filterState.date, ["YYYY-MM-DD", moment.ISO_8601], true);
    if (!m.isValid()) return null;
    return m.toDate();
  }, [filterState.date]);

  const setDate = useCallback(
    (val) => {
      if (!val) {
        setFilterState({ date: "" });
        return;
      }
      const next = val instanceof Date ? moment(val) : moment(val);
      setFilterState({ date: next.isValid() ? next.format("YYYY-MM-DD") : "" });
    },
    [setFilterState],
  );

  const searchTrim = useMemo(
    () => (searchInputValue || "").trim(),
    [searchInputValue],
  );

  const shouldShowKeywordDropdown = isKeywordOpen && Boolean(searchTrim);

  const isOverlayOpen =
    isRegionOpen || isDatesOpen || isGuestsOpen || shouldShowKeywordDropdown;

  useEffect(() => {
    if (!isOverlayOpen) return;
    if (typeof window === "undefined") return;

    const getPopoverEls = () =>
      [
        regionPopoverRef.current,
        datesPopoverRef.current,
        guestsPopoverRef.current,
        keywordPopoverRef.current,
      ].filter(Boolean);

    const findPopoverRoot = (target) => {
      const popovers = getPopoverEls();
      for (const el of popovers) {
        if (el && el.contains(target)) return el;
      }
      return null;
    };

    const findScrollableAncestor = (from, until) => {
      let node = from;
      while (node && node !== until && node instanceof HTMLElement) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const isScrollable =
          (overflowY === "auto" || overflowY === "scroll") &&
          node.scrollHeight > node.clientHeight;
        if (isScrollable) return node;
        node = node.parentElement;
      }
      return null;
    };

    const canScrollInDirection = (scrollable, deltaY) => {
      if (!scrollable) return false;
      const maxScrollTop = scrollable.scrollHeight - scrollable.clientHeight;
      if (maxScrollTop <= 0) return false;
      if (deltaY > 0) return scrollable.scrollTop < maxScrollTop;
      if (deltaY < 0) return scrollable.scrollTop > 0;
      return true;
    };

    const shouldAllowScroll = (target, deltaY) => {
      const root = findPopoverRoot(target);
      if (!root) return false;
      const scrollable = findScrollableAncestor(target, root);
      return canScrollInDirection(scrollable, deltaY);
    };

    const onWheel = (e) => {
      const deltaY = Number(e.deltaY || 0);
      if (shouldAllowScroll(e.target, deltaY)) return;
      e.preventDefault();
    };

    const onTouchStart = (e) => {
      const y = e.touches?.[0]?.clientY;
      touchLastYRef.current = Number.isFinite(Number(y)) ? Number(y) : 0;
    };

    const onTouchMove = (e) => {
      const y = e.touches?.[0]?.clientY;
      const currentY = Number.isFinite(Number(y))
        ? Number(y)
        : touchLastYRef.current;
      const prevY = touchLastYRef.current;
      touchLastYRef.current = currentY;
      const deltaY = prevY - currentY;

      if (shouldAllowScroll(e.target, deltaY)) return;
      e.preventDefault();
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [isOverlayOpen]);

  const regionPresence = usePresenceTransition(isRegionOpen, 180);
  const datesPresence = usePresenceTransition(isDatesOpen, 180);
  const guestsPresence = usePresenceTransition(isGuestsOpen, 180);
  const keywordPresence = usePresenceTransition(shouldShowKeywordDropdown, 180);

  const isCompact = useHeaderCompact(headerRef, {
    throttleMs: 120,
    hysteresis: 120,
  });

  useEffect(() => {
    document.body.classList.toggle("is-compact", isCompact);
  }, [isCompact]);

  const regionPopoverPos = usePopoverPosition(isRegionOpen, {
    headerRef,
    searchRef,
    compute: ({ headerRect, searchRect }) => {
      const left = Math.max(0, searchRect.left - headerRect.left);
      const top = Math.max(0, searchRect.bottom - headerRect.top + 4);
      return { left, top };
    },
    throttleMs: 80,
    deps: [isCompact],
  });

  const datesPopoverPos = usePopoverPosition(isDatesOpen, {
    headerRef,
    searchRef,
    compute: ({ headerRect, searchRect }) => {
      const left = Math.max(0, searchRect.left - headerRect.left);
      const top = Math.max(0, searchRect.bottom - headerRect.top + 4);
      const width = Math.max(0, searchRect.width);
      return { left, top, width };
    },
    throttleMs: 80,
    deps: [isCompact],
  });

  const guestsPopoverPos = usePopoverPosition(isGuestsOpen, {
    headerRef,
    searchRef,
    compute: ({ headerRect, searchRect }) => {
      const fieldRect = guestsBtnRef.current
        ? guestsBtnRef.current.getBoundingClientRect()
        : null;

      if (!fieldRect) return { left: 0, top: 0 };

      const left = Math.max(
        0,
        fieldRect.left - headerRect.left + fieldRect.width / 2,
      );
      const top = Math.max(0, searchRect.bottom - headerRect.top + 4);
      return { left, top };
    },
    throttleMs: 80,
    deps: [isCompact],
    initialRafCount: 1,
  });

  const keywordPopoverPos = usePopoverPosition(shouldShowKeywordDropdown, {
    headerRef,
    searchRef,
    compute: ({ headerRect, searchRect }) => {
      const fieldRect = fieldRef.current
        ? fieldRef.current.getBoundingClientRect()
        : null;

      const leftSource = fieldRect || searchRect;
      const left = Math.max(0, leftSource.left - headerRect.left);
      const top = Math.max(0, searchRect.bottom - headerRect.top + 4);
      const width = Math.max(0, leftSource.width);
      return { left, top, width };
    },
    throttleMs: 80,
    deps: [isCompact],
  });

  useDismissOnOutsidePointer(isRegionOpen, {
    refs: [regionPopoverRef, regionBtnRef],
    onDismiss: () => setIsRegionOpen(false),
  });

  useDismissOnOutsidePointer(isDatesOpen, {
    refs: [datesPopoverRef, datesBtnRef],
    onDismiss: () => setIsDatesOpen(false),
  });

  useDismissOnOutsidePointer(isGuestsOpen, {
    refs: [guestsPopoverRef, guestsBtnRef],
    onDismiss: () => setIsGuestsOpen(false),
  });

  useDismissOnOutsidePointer(shouldShowKeywordDropdown, {
    refs: [keywordPopoverRef, searchRef],
    onDismiss: () => setIsKeywordOpen(false),
  });

  const { handleRegionToggle, handleDatesToggle, handleGuestsToggle } =
    useExclusivePopoverToggles({
      setIsRegionOpen,
      setIsDatesOpen,
      setIsGuestsOpen,
    });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    if (value.trim()) {
      setIsKeywordOpen(true);
    } else {
      setIsKeywordOpen(false);
    }
  };

  const handleSearchFocus = () => {
    const q = (searchInputValue || "").trim();
    setInputFocus(true);
    if (q) {
      setIsKeywordOpen(true);
    }
  };

  const dateFormat = useMemo(() => {
    if (!date) return t("Header.Dates");
    if (moment.locale().startsWith("zh")) return moment(date).format("MMMD日");
    return moment(date).format("MMM D");
  }, [date]);

  return (
    <header
      ref={headerRef}
      className={`ui-head-bar ui-headbar ${isCompact ? "is-compact" : ""}`}
      role="banner"
    >
      <div className="ui-head-bar__top">
        <div className="ui-head-bar__left">
          <a
            className="ui-head-bar__logo"
            onClick={onHomeClick}
            aria-label="DiningCity Home"
          >
            <img src={logo?.web_logo || ""} />
          </a>
        </div>

        <nav className="ui-head-bar__nav" aria-label="Primary">
          <button
            type="button"
            className={`ui-head-bar__navItem ${
              activeTab === "home" ? "is-active" : ""
            }`}
            onClick={onHomeClick}
          >
            {activeTab === "home" ? (
              <HomeActiveIcon className="ui-head-bar__navIcon" />
            ) : (
              <HomeIcon className="ui-head-bar__navIcon" />
            )}
            <span>{t("Header.Home")}</span>
          </button>
          <button
            type="button"
            className={`ui-head-bar__navItem ${
              activeTab === "explore" ? "is-active" : ""
            }`}
            onClick={onExploreClick}
          >
            {activeTab === "explore" ? (
              <SearchActiveIcon className="ui-head-bar__navIcon" />
            ) : (
              <SearchIcon className="ui-head-bar__navIcon" />
            )}
            <span>{t("Header.Browse")}</span>
          </button>
        </nav>

        <div className="ui-head-bar__right">
          <button
            type="button"
            className="ui-head-bar__action"
            onClick={onRestaurantLoginClick}
          >
            {t("Header.RestaurantLog")}
          </button>
          <button
            type="button"
            className="ui-head-bar__action"
            onClick={onPartnerClick}
          >
            {t("Header.PartnerWithUs")}
          </button>
          <button
            type="button"
            className="ui-head-bar__action ui-head-bar__action--icon"
            aria-label="User"
            onClick={onDownloadClick}
          >
            <PhoneIcon />
          </button>
          <button
            type="button"
            className="ui-head-bar__action ui-head-bar__action--icon"
            style={{ border: 0 }}
            aria-label="User"
            onClick={onUserClick}
          >
            <ProfileIcon />
          </button>
        </div>
      </div>

      <div className="ui-head-bar__navSecondary">
        <div className="ui-head-bar__left">
          <a
            className="ui-head-bar__logo"
            onClick={onHomeClick}
            aria-label="DiningCity Home"
          >
            <img src={logo?.web_logo || ""} />
          </a>
        </div>
        <div className="ui-head-bar__search" role="search" ref={searchRef}>
          <button
            type="button"
            className="ui-head-bar__segment ui-head-bar__segment--first"
            onClick={handleRegionToggle}
            ref={regionBtnRef}
            aria-haspopup="dialog"
            aria-expanded={isRegionOpen}
            style={{
              textTransform: "capitalize",
              color: pageCity?.name ? "#252525" : "#888",
            }}
          >
            <LocationIcon className="ui-head-bar__segmentIcon" />
            <span className="ui-head-bar__segmentText">
              {pageCity?.name || "Region"}
            </span>
          </button>

          <button
            type="button"
            className="ui-head-bar__segment"
            onClick={handleDatesToggle}
            ref={datesBtnRef}
            aria-haspopup="dialog"
            aria-expanded={isDatesOpen}
            style={{
              textTransform: "capitalize",
              color: date ? "#252525" : "#888",
            }}
          >
            <CalendarIcon className="ui-head-bar__segmentIcon" />
            <span className="ui-head-bar__segmentText">{dateFormat}</span>
            {!!date && (
              <div
                className="ui-head-bar__close2Icon"
                onClick={(evt) => {
                  evt.stopPropagation();
                  setFilterState({ date: "" });
                }}
              >
                <Close2Icon />
              </div>
            )}
          </button>

          <button
            type="button"
            className="ui-head-bar__segment"
            onClick={handleGuestsToggle}
            ref={guestsBtnRef}
            aria-haspopup="dialog"
            aria-expanded={isGuestsOpen}
            style={{
              textTransform: "capitalize",
              color: guestsCount > 0 ? "#252525" : "#888",
            }}
          >
            <AddGroupIcon className="ui-head-bar__segmentIcon" />
            <span className="ui-head-bar__segmentText">
              {guestsCount > 0
                ? t("Header.{count} Guests", { count: guestsCount })
                : t("Header.Guests")}
            </span>
            {guestsCount > 0 && (
              <div
                className="ui-head-bar__close2Icon"
                onClick={(evt) => {
                  evt.stopPropagation();
                  setGuestsCount(0);
                }}
              >
                <Close2Icon />
              </div>
            )}
          </button>

          <div className="ui-head-bar__field" ref={fieldRef}>
            <input
              className="ui-head-bar__input"
              type="search"
              placeholder={t("Header.SearchRestaurants")}
              aria-label={t("Header.SearchRestaurants")}
              ref={searchInputRef}
              value={searchInputValue}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
            />

            {!!searchInputValue && (
              <CloseIcon
                className="ui-head-bar__closeIcon"
                size={20}
                color="#252525"
                onClick={() => {
                  handleSearchChange({ target: { value: "" } });
                }}
              />
            )}
            <button
              type="button"
              className={cn("ui-head-bar__fieldBtn", {
                active: Boolean(searchInputValue),
              })}
              aria-label="Search"
              onClick={() => {
                setIsKeywordOpen(false);
                onSearchBtnClick(searchInputValue);
              }}
            >
              <SearchIcon className="ui-head-bar__fieldIcon" size={22} />
            </button>
          </div>
        </div>

        <div className="ui-head-bar__right">
          <ProfileIcon
            size={44}
            type="button"
            className="ui-head-bar__action ui-head-bar__action--icon"
            style={{ border: 0 }}
            aria-label="User"
            onClick={onUserClick}
          ></ProfileIcon>
        </div>
      </div>

      {isOverlayOpen && (
        <div className="ui-head-bar__mask" aria-hidden="true" />
      )}

      {regionPresence.isPresent && (
        <div
          className={`ui-head-bar__regionPopover ui-head-bar__popoverAnim is-${regionPresence.phase}`}
          ref={regionPopoverRef}
          role="dialog"
          aria-label="Region picker"
          style={{ left: regionPopoverPos.left, top: regionPopoverPos.top }}
        >
          <RegionPicker
            regions={regionList}
            currentCity={currentCity}
            searchRegion={searchRegion}
            searchResults={searchRegionList}
            getPosition={getPosition}
            onCityClick={(city) => {
              onRegionClick(city);
            }}
          />
        </div>
      )}

      {keywordPresence.isPresent && (
        <div
          className={`ui-head-bar__keywordPopover ui-head-bar__popoverAnim is-${keywordPresence.phase}`}
          ref={keywordPopoverRef}
          role="listbox"
          aria-label="Search suggestions"
          style={{
            left: keywordPopoverPos.left,
            top: keywordPopoverPos.top,
            width: keywordPopoverPos.width,
          }}
        >
          <KeywordPicker
            query={searchInputValue}
            onSelect={(item) => {
              setIsKeywordOpen(false);
              setSearchInputValue(item?.label || "");
              onSearchBtnClick(item?.label);
            }}
          />
        </div>
      )}

      {datesPresence.isPresent && (
        <div
          className={`ui-head-bar__datesPopover ui-head-bar__popoverAnim is-${datesPresence.phase}`}
          ref={datesPopoverRef}
          role="dialog"
          aria-label="Date picker"
          style={{
            left: datesPopoverPos.left,
            top: datesPopoverPos.top,
            width: datesPopoverPos.width,
          }}
        >
          <CalendarPicker
            value={date}
            onChange={(val) => {
              setDate(val);
              setTimeout(() => setIsDatesOpen(false), 500);
            }}
          />
        </div>
      )}

      {guestsPresence.isPresent && (
        <div
          className={`ui-head-bar__guestsPopover ui-head-bar__popoverAnimCenterX is-${guestsPresence.phase}`}
          ref={guestsPopoverRef}
          role="dialog"
          aria-label="Guests picker"
          style={{ left: guestsPopoverPos.left, top: guestsPopoverPos.top }}
        >
          <SeatPicker value={guestsCount} onChange={setGuestsCount} />
        </div>
      )}
    </header>
  );
};

export default observer(DesktopHeadBar);
